<template>
  <div class="online-appointments-page">
    <div v-if="loading" class="oa-loading">
      <div class="oa-loading-inner">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <span>Loading settings...</span>
      </div>
    </div>

    <div v-else-if="error && !loaded" class="oa-error">
      <i class="fa fa-exclamation-triangle fa-2x text-danger"></i>
      <h5>Failed to load settings</h5>
      <p>{{ error }}</p>
      <button class="oa-btn oa-btn-primary" @click="loadData">Retry</button>
    </div>

    <div v-else-if="loaded">
      <!-- Top-level pill tabs -->
      <div class="oa-top-tabs">
        <div
          class="oa-top-tab"
          :class="{ active: activeTab === 'dashboard' }"
          @click="activeTab = 'dashboard'"
        >
          <i class="fa fa-bar-chart"></i> Online Appointments
        </div>
        <div
          class="oa-top-tab"
          :class="{ active: activeTab === 'settings' }"
          @click="activeTab = 'settings'"
        >
          <i class="fa fa-cog"></i> Settings
        </div>
      </div>

      <!-- Dashboard Tab -->
      <OnlineAppointmentsDashboard
        v-if="activeTab === 'dashboard'"
        :siteID="siteID"
        :providers="providers"
        :appointmentTypes="settings.appointmentTypes || []"
      />

      <!-- Settings Tab -->
      <div v-if="activeTab === 'settings'" class="oa-container">
        <!-- Left Navigation -->
        <div class="oa-sidebar">
          <div class="oa-sidebar-header">
            <i class="fa fa-cog"></i> Settings
          </div>
          <div class="oa-sidebar-nav">
            <div
              class="oa-nav-item"
              :class="{ active: activeSection === 'general' }"
              @click="activeSection = 'general'"
            >
              <i class="fa fa-sliders"></i>
              <span>General</span>
            </div>
            <div
              class="oa-nav-item"
              :class="{ active: activeSection === 'appointment-types' }"
              @click="activeSection = 'appointment-types'"
            >
              <i class="fa fa-calendar-plus-o"></i>
              <span>Appointment Types</span>
            </div>
            <div
              class="oa-nav-item"
              :class="{ active: activeSection === 'preview' }"
              @click="activeSection = 'preview'"
            >
              <i class="fa fa-globe"></i>
              <span>Preview Booking Page</span>
            </div>
          </div>
        </div>

        <!-- Right Content -->
        <div class="oa-content">
          <GeneralSettings
            v-show="activeSection === 'general'"
            :settings="settings"
            :siteName="currentSiteName"
            :paidLocations="paidLocations"
            :locationStatus="locationStatus"
            :blockoutTypes="blockoutTypes"
            :confirmationStatuses="confirmationStatuses"
            :procedureCodes="procedureCodes"
            :providers="providers"
            :currentSiteId="generalSelectedSiteId"
            :saving="saving"
            :effectiveSchedulingWindow="effectiveSchedulingWindow"
            @save="onSaveGeneral"
            @location-changed="onGeneralLocationChanged"
          />
          <AppointmentTypes
            v-show="activeSection === 'appointment-types'"
            :appointmentTypes="settings.appointmentTypes || []"
            :procedureCodes="procedureCodes"
            :providers="providers"
            :operatories="operatories"
            :blockoutTypes="blockoutTypes"
            :paidLocations="paidLocations"
            :locationStatus="locationStatus"
            :currentSiteId="appointmentTypesSelectedSiteId"
            :saving="saving"
            @save="onSaveAppointmentTypes"
            @location-changed="onAppointmentTypesLocationChanged"
            @link-copied="$toast.success('Booking link copied to clipboard!')"
          />
          <PreviewCalendar
            v-show="activeSection === 'preview'"
            :siteID="previewSelectedSiteId"
            :paidLocations="paidLocations"
            :currentSiteId="previewSelectedSiteId"
            @location-changed="onPreviewLocationChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import mixin from '@/mixin';
import GeneralSettings from './GeneralSettings.vue';
import AppointmentTypes from './AppointmentTypes.vue';
import PreviewCalendar from './PreviewCalendar.vue';
import OnlineAppointmentsDashboard from './OnlineAppointmentsDashboard.vue';

export default {
  name: 'OnlineAppointments',
  components: {
    GeneralSettings,
    AppointmentTypes,
    PreviewCalendar,
    OnlineAppointmentsDashboard
  },
  mixins: [mixin],

  data() {
    return {
      activeTab: 'dashboard',
      activeSection: 'general',
      // Each tab tracks its own selected location (independent)
      generalSelectedSiteId: '',
      appointmentTypesSelectedSiteId: '',
      previewSelectedSiteId: ''
    };
  },

  computed: {
    settings() {
      return this.$store.state.onlineAppointments.settings;
    },
    procedureCodes() {
      return this.$store.state.onlineAppointments.procedureCodes;
    },
    providers() {
      return this.$store.state.onlineAppointments.providers;
    },
    operatories() {
      return this.$store.state.onlineAppointments.operatories;
    },
    locations() {
      return this.$store.state.onlineAppointments.locations;
    },
    blockoutTypes() {
      return this.$store.state.onlineAppointments.blockoutTypes;
    },
    confirmationStatuses() {
      return this.$store.state.onlineAppointments.confirmationStatuses;
    },
    // Server-computed booking window; null on an older backend (GeneralSettings tolerates it)
    effectiveSchedulingWindow() {
      return this.$store.state.onlineAppointments.effectiveSchedulingWindow;
    },
    showLocationSelection() {
      return this.$store.state.onlineAppointments.showLocationSelection;
    },
    loading() {
      return this.$store.state.onlineAppointments.loading;
    },
    saving() {
      return this.$store.state.onlineAppointments.saving;
    },
    error() {
      return this.$store.state.onlineAppointments.error;
    },
    loaded() {
      return this.$store.state.onlineAppointments.loaded;
    },
    // Site name for currently selected location in General tab
    currentSiteName() {
      const targetSiteId = this.generalSelectedSiteId || this.siteID;
      // First try to find in paidLocations
      const loc = this.paidLocations.find(l => l.siteId === targetSiteId);
      if (loc) return loc.name;
      // Fallback to getSite
      const site = this.getSite(targetSiteId);
      return site ? (site.SiteName || site.Name || site.name || '') : '';
    },
    // Paid locations for this customerID (locations with ONLINE_SCHEDULING_PAID_FOR = TRUE)
    paidLocations() {
      return this.$store.state.onlineAppointments.paidLocations || [];
    },
    // Location status for all locations (configured / needs setup)
    locationStatus() {
      return this.$store.state.onlineAppointments.locationStatus || {};
    }
  },

  created() {
    // Initialize selected site IDs to current session siteID
    this.generalSelectedSiteId = this.siteID;
    this.appointmentTypesSelectedSiteId = this.siteID;
    this.previewSelectedSiteId = this.siteID;
    this.loadData();
  },

  methods: {
    loadData() {
      if (this.siteID) {
        this.$store.dispatch('onlineAppointments/loadSettings', { siteID: this.siteID });
        // Load paid locations for this customer (all SiteIDs with ONLINE_SCHEDULING_PAID_FOR = TRUE)
        if (this.customerID) {
          this.$store.dispatch('onlineAppointments/loadPaidLocations', { customerID: this.customerID, siteID: this.siteID });
        }
      }
    },

    // Handle location change in General Settings tab
    async onGeneralLocationChanged(newSiteId) {
      this.generalSelectedSiteId = newSiteId;
      await this.$store.dispatch('onlineAppointments/loadSettingsForLocation', {
        siteID: newSiteId,
        forceReload: false
      });
    },

    // Handle location change in Appointment Types tab
    async onAppointmentTypesLocationChanged(newSiteId) {
      this.appointmentTypesSelectedSiteId = newSiteId;
      await this.$store.dispatch('onlineAppointments/loadSettingsForLocation', {
        siteID: newSiteId,
        forceReload: false
      });
    },

    // Handle location change in Preview tab
    onPreviewLocationChanged(newSiteId) {
      this.previewSelectedSiteId = newSiteId;
      // Preview tab doesn't need to load settings, just change the iframe URL
    },

    async onSaveGeneral(updatedGeneralFields) {
      try {
        const targetSiteId = this.generalSelectedSiteId || this.siteID;
        // Use per-site cache as merge base (not global state.settings which may be from a different location)
        const cached = this.$store.getters['onlineAppointments/getCachedSettings'](targetSiteId);
        const baseSettings = cached ? cached.settings : {};
        const mergedSettings = { ...baseSettings, ...updatedGeneralFields };
        await this.$store.dispatch('onlineAppointments/saveSettings', {
          siteID: targetSiteId,
          settings: mergedSettings
        });

        // Propagate locationMode to all other paid locations (customer-level setting)
        if (updatedGeneralFields.locationMode && this.paidLocations.length > 1) {
          const otherLocations = this.paidLocations.filter(loc => (loc.id || loc.siteId) !== targetSiteId);
          for (const loc of otherLocations) {
            const locSiteId = loc.id || loc.siteId;
            const locCached = this.$store.getters['onlineAppointments/getCachedSettings'](locSiteId);
            if (locCached) {
              const locSettings = { ...locCached.settings, locationMode: updatedGeneralFields.locationMode, allowPatientLocationChoice: updatedGeneralFields.allowPatientLocationChoice };
              await this.$store.dispatch('onlineAppointments/saveSettings', { siteID: locSiteId, settings: locSettings, skipGlobalState: true });
            }
          }
        }

        this.$toast.success('Settings updated successfully!');
      } catch (err) {
        // The save handler refuses a config that can never fire (e.g. auto-extend on with
        // no valid month count) with a 400 that explains why — show that, not a generic
        // "Failed to save settings." the office can do nothing with.
        this.$toast.error(err?.response?.data?.message || 'Failed to save settings.');
      }
    },

    async onSaveAppointmentTypes(updatedAppointmentTypes) {
      try {
        const targetSiteId = this.appointmentTypesSelectedSiteId || this.siteID;
        // Use per-site cache as merge base (not global state.settings which may be from a different location)
        const cached = this.$store.getters['onlineAppointments/getCachedSettings'](targetSiteId);
        const baseSettings = cached ? cached.settings : {};
        const mergedSettings = { ...baseSettings, appointmentTypes: updatedAppointmentTypes };
        await this.$store.dispatch('onlineAppointments/saveSettings', {
          siteID: targetSiteId,
          settings: mergedSettings
        });
        this.$toast.success('Appointment types saved!');
      } catch (err) {
        this.$toast.error('Failed to save appointment types.');
      }
    }
  },

  watch: {
    siteID(newVal) {
      if (newVal && !this.loaded) {
        this.loadData();
      }
    }
  }
};
</script>

<style lang="scss" src="./OnlineAppointments.scss"></style>
